import Input from "./input";
import PropTypes from "prop-types";
import {useContext, useEffect, useState} from "react";
import {FormContext} from "../../index";
import getSymbolFromCurrency from 'currency-symbol-map'

const {prefix, suffix, type, pattern, ...inputPropTypes} = Input.propTypes;
const {...inputDefaultProps} = Input.defaultProps;

// Currencies with always-front placement (symbol always before the number)
const SYMBOL_ALWAYS_FRONT = [
    "USD", "CAD", "AUD", "NZD", "GBP", "JPY", "CHF", "HKD", "SGD", "TWD", "CNY", "MYR", "INR", "KRW", "IDR", "THB", "MXN",
    "ILS", "PHP", "BRL", "CLP", "ARS", "TRY", "ZAR", "EGP", "LBP", "UAH", "GEL", "KZT", "VND", "PKR", "OMR", "SAR", "QAR",
    "AED", "MAD", "DZD", "JOD", "MOP", "BHD", "KWD", "TND", "NGN"
];

// Currencies with always-after placement (symbol always after the number, with a space)
const SYMBOL_ALWAYS_AFTER = [
    "SEK", "NOK", "DKK", "HUF", "PLN", "CZK", "RUB", "BYN", "RSD", "BGN", "RON"
];

// Ambiguous placement: currency + country determines placement (EUR is the only major ambiguous case)
const SYMBOL_AMBIGUOUS = {
    EUR: {
        // Countries with symbol after number
        after: [
            "FR", // France
            "DE", // Germany
            "ES", // Spain
            "IT", // Italy
            "FI", // Finland
            "BE", // Belgium
            "NL", // Netherlands
            "LU", // Luxembourg
            "GR", // Greece
            "PT", // Portugal
            "SI", // Slovenia
            "SK", // Slovakia
            "EE", // Estonia
            "LT", // Lithuania
            "LV", // Latvia
            "MC", // Monaco
            "SM", // San Marino
            "VA", // Vatican City
            "AD", // Andorra
            "MT", // Malta
        ],
        // Countries with symbol before number
        before: [
            "IE", // Ireland
            "CY", // Cyprus
        ],
        // Default placement if country not listed
        common: "after",
    }
};

function getCurrencySymbol(currency) {
    return getSymbolFromCurrency(currency) || '';
}

// --- Main Placement Logic ---

/**
 * @param {string} currency Currency code (e.g., "EUR", "USD", "JPY")
 * @param {string} country  ISO 3166-1 alpha-2 country code (e.g., "FR", "IE")
 * @returns {"before"|"after"}
 */
function getSymbolPlacement(currency, country) {
    if (SYMBOL_ALWAYS_FRONT.includes(currency)) return "before";
    if (SYMBOL_ALWAYS_AFTER.includes(currency)) return "after";
    const ambiguous = SYMBOL_AMBIGUOUS[currency];
    if (ambiguous) {
        if (country) {
            if (ambiguous.before?.includes(country)) return "before";
            if (ambiguous.after?.includes(country)) return "after";
        }
        return ambiguous.common || "before";
    }
    // Fallback: before for unknowns
    return "before";
}

function formatCurrency(value, currency, country, valueAsCents, emptyPlaceholder) {
    value = value === undefined || value === null || typeof value !== 'number' ? '' : Number(value);
    if (isNaN(value)) value = '';

    if (value !== '' && valueAsCents && value > 0) {
        value = value / 100;
    }

    const fractionDigits = value && value !== Math.floor(value) ? 2 : 0;
    if (value === '') return emptyPlaceholder;

    const symbol = getCurrencySymbol(currency);
    const formattedNumber = value.toLocaleString('en', {
        minimumFractionDigits: fractionDigits,
        maximumFractionDigits: fractionDigits
    });

    const placement = getSymbolPlacement(currency, country);

    if (placement === "before") return `${symbol}${formattedNumber}`;
    if (placement === "after") return `${formattedNumber} ${symbol}`;
    return `${symbol}${formattedNumber}`; // Fallback
}

// --- Exported helpers ---
export const getCurrencyLabel = function (value, currency, country, valueAsCents, emptyPlaceholder) {
    return formatCurrency(value, currency, country, valueAsCents, emptyPlaceholder);
};

export const getCurrencyValue = function (value, valueAsCents, valueAsDollars, emptyPlaceholder) {
    value = value === undefined || value === null || typeof value !== 'number' ? '' : Number(value);
    if (isNaN(value)) value = '';

    if (value !== '' && valueAsCents && value > 0) {
        value = value / 100;
    }
    if (value !== '' && valueAsDollars && value > 0) {
        value = value * 100;
    }
    const fractionDigits = value && value !== Math.floor(value) ? 2 : 0;
    return value ? value.toFixed(fractionDigits) : emptyPlaceholder;
};

Currency.propTypes = {
    ...inputPropTypes,
    locale: PropTypes.string,
    currency: PropTypes.string,
    country: PropTypes.string,
    valueAsCents: PropTypes.bool,
    emptyPlaceholder: PropTypes.string,
    hidePrefix: PropTypes.bool
};

Currency.defaultProps = {
    ...inputDefaultProps,
    inlineAddons: false,
    hidePrefix: false,
    pattern: "[-]?\\d+([.]\\d{1,2})?",
    placeholder: "0.00",
    locale: "en-AU",
    currency: 'AUD',
    country: undefined,
    valueAsCents: true,
    emptyPlaceholder: '\u2014'
};

Currency.WebsiteProductDisplayView = function (props) {
    const currency = props.currency ?? props.context?.currency ?? "AUD";
    const country = props.country ?? props.context?.country;
    const formatted = formatCurrency(props.value, currency, country, props.valueAsCents, props.emptyPlaceholder);

    return (
        <div className="grid grid-cols-2 gap-x-4 items-start mb-1">
            <div className="font-semibold col-span-1">{props.label}</div>
            <div className="col-span-1">{props.prefix} {formatted || '—'} {props.suffix}</div>
        </div>
    );
};

Currency.WebsiteProductDisplayView.propTypes = {
    value: PropTypes.any,
    locale: PropTypes.string,
    currency: PropTypes.string,
    country: PropTypes.string,
    valueAsCents: PropTypes.bool,
    emptyPlaceholder: PropTypes.string,
};
Currency.WebsiteProductDisplayView.defaultProps = {
    valueAsCents: true,
    emptyPlaceholder: '\u2014'
};

Currency.DisplayValue = function (props) {
    const currency = props.currency ?? props.context?.currency ?? "AUD";
    const country = props.country ?? props.context?.country;
    return formatCurrency(props.value, currency, country, props.valueAsCents, props.emptyPlaceholder);
};

Currency.DisplayValue.propTypes = {
    value: PropTypes.any,
    locale: PropTypes.string,
    currency: PropTypes.string,
    country: PropTypes.string,
    valueAsCents: PropTypes.bool,
    emptyPlaceholder: PropTypes.string,
};
Currency.DisplayValue.defaultProps = {
    locale: "en-AU",
    currency: 'AUD',
    country: undefined,
    valueAsCents: true,
    emptyPlaceholder: '\u2014'
};

Currency.DisplayView = Currency.DisplayValue;

export default function Currency(props) {
    const context = useContext(FormContext);
    const currency = context?.appContext ? context.appContext.currency : props.currency;
    const country = context?.appContext ? context.appContext.country : props.country;
    const prefix = props.hidePrefix ? '' : getCurrencySymbol(currency);

    let value = props.value === undefined || props.value === null || typeof props.value !== 'number' ? '' : Number(props.value);
    if (props.value && typeof +props.value[0] === 'number') value = Number(props.value);
    if (isNaN(value)) value = '';

    if (value !== '' && props.valueAsCents && value > 0) {
        value = value / 100;
    }

    const fractionDigits = value && value !== Math.floor(value) ? 2 : 0;

    const [input, setInput] = useState(value ? value.toFixed(fractionDigits) : value);

    useEffect(() => {
        setInput(value ? value.toFixed(fractionDigits) : value)
    }, [props.value])

    return (
        <Input
            {...props}
            name={""}
            prefix={prefix}
            value={input}
            onKeyDown={(e) => {
                if (props.readOnly || props.disabled) return;

                // Handles when arrow keys are pressed to increment or decrement the current value
                // When alt or shift is used in combination with the arrow keys then it changes the increment size

                let increment = 1;

                if (e.altKey === true && e.shiftKey === false) {
                    increment = 0.1;
                }

                if (e.shiftKey === true && e.altKey === false) {
                    increment = 10;
                }

                // ArrowUp or ArrowDown
                if (e.keyCode === 38 || e.keyCode === 40) {

                    // ArrowDown decrements so change the increment to a decrement
                    if (e.keyCode === 40) {
                        increment = increment * -1;
                    }

                    const fractionDigits = e.target.value.includes('.') ? 2 : 0;

                    let value = Number(Number(e.target.value.replace(/[^.\d-]/gi, '') || 0).toFixed(fractionDigits));
                    value = value + increment;

                    setInput(value.toFixed(fractionDigits));

                    if (props.valueAsCents) {
                        value = value * 100;
                    }

                    props.onChange(e, props.name, Number(value.toFixed(fractionDigits)));
                }
            }}
            onChange={(e, name, value) => {
                value = value.replace(/[^.\d-]/gi, '');

                // Set the state of the display value to a value including only the currency characters
                setInput(value);

                // Take the string and parse it into a number so we can set the value in the form context 
                // to of type number not of type string
                if (value) {
                    value = Number(value);
                    if (props.valueAsCents) {
                        value = value * 100;
                    }
                    props.onChange(e, props.name, Number(value.toFixed(2)));
                } else {
                    props.onChange(e, props.name, null);
                }
            }}
        />
    );
}